// ABOUTME: Orchestrates WhatsApp pre-registration settings and audited link actions.
// ABOUTME: Keeps privileged requests, CAS reloads, and rollout state in one surface.
import React from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { clearWhatsappConversationQueryCache } from "customHooks/useApi/authCacheIsolation";
import WhatsappPreRegistrationConfigEditor from "./WhatsappPreRegistrationConfigEditor";
import WhatsappPreRegistrationCoverageDiagnosticAlert from "./WhatsappPreRegistrationCoverageDiagnosticAlert";
import WhatsappPreRegistrationHelpControl from "./WhatsappPreRegistrationHelpControl";
import { getWhatsappPreRegistrationLabels } from "./whatsappPreRegistrationLabels";
import WhatsappPreRegistrationLinks from "./WhatsappPreRegistrationLinks";
import WhatsappPreRegistrationSetupGuide from "./WhatsappPreRegistrationSetupGuide";
import {
    useWhatsappPreRegistrationCoverageDiagnostic
} from "./useWhatsappPreRegistrationCoverageDiagnostic";
import {
    loadWhatsappPreRegistrationSettings,
    revokeWhatsappPreRegistrationLink,
    rotateWhatsappPreRegistrationLink,
    saveWhatsappPreRegistrationSettings
} from "./whatsappPreRegistrationSettingsApi";
import {
    createWhatsappPreRegistrationSettingsState,
    getWhatsappPreRegistrationConfigValidationError,
    getWhatsappPreRegistrationSetupGuide,
    isWhatsappPreRegistrationConfigDirty,
    requestWhatsappPreRegistrationRolloutChange,
    WhatsappPreRegistrationConfig
} from "./whatsappPreRegistrationSettingsModel";

export const resolveWhatsappPreRegistrationErrorMessage = (
    error: any,
    translate: (key: string) => string,
    fallback: string
): string => {
    const rawTranslationCode = error?.response?.data?.translationCode;
    const translationCode =
        typeof rawTranslationCode === "string" ? rawTranslationCode.trim() : "";
    if (translationCode) {
        const translated = translate(translationCode);
        if (translated && translated !== translationCode) {
            return translated;
        }
        return String(fallback || "");
    }

    const rawMessage = error?.response?.data?.message;
    if (typeof rawMessage === "string" && rawMessage.trim()) {
        const trimmedMessage = rawMessage.trim();
        const translated = translate(trimmedMessage);
        if (translated && translated !== trimmedMessage) {
            return translated;
        }
        return trimmedMessage;
    }

    const rawErrorMessage = error?.response?.data?.error?.message;
    if (typeof rawErrorMessage === "string" && rawErrorMessage.trim()) {
        const trimmedErrorMessage = rawErrorMessage.trim();
        const translated = translate(trimmedErrorMessage);
        if (translated && translated !== trimmedErrorMessage) {
            return translated;
        }
        return trimmedErrorMessage;
    }

    if (typeof error?.message === "string" && error.message.trim()) {
        const trimmed = error.message.trim();
        const translated = translate(trimmed);
        if (translated && translated !== trimmed) {
            return translated;
        }
        return trimmed;
    }

    return String(fallback || "");
};

interface WhatsappPreRegistrationSettingsProps {
    companyId: number;
    featureActive: boolean;
    canManageFeature: boolean;
    changingFeature: boolean;
    onFeatureActiveChange: (enabled: boolean) => Promise<void>;
}

const WhatsappPreRegistrationSettings = ({
    companyId,
    featureActive,
    canManageFeature,
    changingFeature,
    onFeatureActiveChange
}: WhatsappPreRegistrationSettingsProps) => {
    const queryClient = useQueryClient();
    const [t] = useTranslation("JOBSHAREMODULE", { useSuspense: false });
    const labels = React.useMemo(
        () =>
            getWhatsappPreRegistrationLabels((key, values) =>
                String(t(key, values))
            ),
        [t]
    );
    const getErrorMessage = React.useCallback(
        (error: any, fallback: string) =>
            resolveWhatsappPreRegistrationErrorMessage(error, t, fallback),
        [t]
    );
    const [settings, setSettings] = React.useState(() =>
        createWhatsappPreRegistrationSettingsState({
            config: null,
            options: {}
        })
    );
    const [savedSettings, setSavedSettings] = React.useState(() =>
        createWhatsappPreRegistrationSettingsState({
            config: null,
            options: {}
        })
    );
    const [links, setLinks] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [loadSucceeded, setLoadSucceeded] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [actingStoreId, setActingStoreId] = React.useState("");
    const [error, setError] = React.useState("");
    const {
        coverageDiagnostic,
        clearDiagnostic,
        clearForConfigEdit,
        captureConfigurationGeneration,
        showCoverageDiagnostic
    } = useWhatsappPreRegistrationCoverageDiagnostic();
    const [notice, setNotice] = React.useState<{
        severity: "success" | "warning" | "info";
        text: string;
    } | null>(null);
    const isDirty = isWhatsappPreRegistrationConfigDirty(
        settings.config,
        savedSettings.config
    );
    const setupGuide = React.useMemo(
        () =>
            getWhatsappPreRegistrationSetupGuide({
                config: settings.config,
                options: settings.options,
                isDirty,
                featureActive
            }),
        [featureActive, isDirty, settings.config, settings.options]
    );

    const applyLoadedSettings = React.useCallback(
        (current: {
            settings: ReturnType<
                typeof createWhatsappPreRegistrationSettingsState
            >;
            links: any[];
        }) => {
            setSettings(current.settings);
            setSavedSettings(current.settings);
            setLinks(current.links);
        },
        []
    );

    const loadSettings = React.useCallback(async () => {
        setLoading(true);
        setLoadSucceeded(false);
        setError("");
        clearDiagnostic();
        try {
            const current = await loadWhatsappPreRegistrationSettings({
                companyId
            });
            applyLoadedSettings(current);
            setLoadSucceeded(true);
        } catch (loadError) {
            setError(getErrorMessage(loadError, labels.loadError));
        } finally {
            setLoading(false);
        }
    }, [
        applyLoadedSettings,
        clearDiagnostic,
        companyId,
        getErrorMessage,
        labels.loadError
    ]);

    React.useEffect(() => {
        void loadSettings();
    }, [loadSettings]);

    React.useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!isDirty) {
                return;
            }
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isDirty]);

    const updateConfig = (config: WhatsappPreRegistrationConfig) => {
        setSettings((current) => ({ ...current, config }));
        setNotice(null);
        clearForConfigEdit();
    };

    const updateFeatureActive = async (enabled: boolean) => {
        const confirmation = enabled
            ? labels.rolloutEnableConfirm
            : labels.rolloutDisableConfirm;
        setError("");
        setNotice(null);
        clearDiagnostic();
        try {
            await requestWhatsappPreRegistrationRolloutChange({
                enabled,
                confirmChange: () => window.confirm(confirmation),
                updateFeatureActive: onFeatureActiveChange,
                reloadSettings: async () => {
                    const current = await loadWhatsappPreRegistrationSettings({
                        companyId
                    });
                    setSettings((previous) => ({
                        ...previous,
                        generalEntry: current.settings.generalEntry
                    }));
                    setSavedSettings((previous) => ({
                        ...previous,
                        generalEntry: current.settings.generalEntry
                    }));
                    setLinks(current.links);
                }
            });
        } catch (featureError) {
            setError(getErrorMessage(featureError, labels.rolloutUpdateError));
            clearDiagnostic();
        }
    };

    const saveSettings = async () => {
        const validationError = getWhatsappPreRegistrationConfigValidationError(
            settings.config
        );
        if (validationError) {
            setError(String(t(validationError)));
            setNotice(null);
            clearDiagnostic();
            return;
        }
        setSaving(true);
        setError("");
        setNotice(null);
        clearDiagnostic();
        const saveGeneration = captureConfigurationGeneration();
        try {
            const result = await saveWhatsappPreRegistrationSettings({
                companyId,
                settings
            });
            await clearWhatsappConversationQueryCache(queryClient);
            setSettings(result.settings);
            if (result.status === "conflict") {
                setSavedSettings(result.settings);
                setLinks(result.links || []);
                setNotice({ severity: "warning", text: labels.conflict });
            } else {
                setSavedSettings(result.settings);
                setLinks(result.links);
                setNotice({ severity: "success", text: labels.saved });
            }
        } catch (saveError) {
            setError(getErrorMessage(saveError, labels.saveError));
            showCoverageDiagnostic(saveError, saveGeneration);
        } finally {
            setSaving(false);
        }
    };

    const rotateLink = async (storeId: string, hasCurrentLink: boolean) => {
        if (isDirty) {
            return;
        }
        if (hasCurrentLink && !window.confirm(labels.rotateConfirm)) {
            return;
        }
        setActingStoreId(storeId);
        setError("");
        clearDiagnostic();
        const linkGeneration = captureConfigurationGeneration();
        try {
            setLinks(
                await rotateWhatsappPreRegistrationLink({
                    companyId,
                    storeId
                })
            );
            setNotice({
                severity: "success",
                text: hasCurrentLink ? labels.linkRotated : labels.linkCreated
            });
        } catch (linkError) {
            setError(getErrorMessage(linkError, labels.linkActionError));
            showCoverageDiagnostic(linkError, linkGeneration);
        } finally {
            setActingStoreId("");
        }
    };

    const revokeLink = async (storeId: string) => {
        if (isDirty) {
            return;
        }
        if (!window.confirm(labels.revokeConfirm)) {
            return;
        }
        setActingStoreId(storeId);
        setError("");
        clearDiagnostic();
        try {
            setLinks(
                await revokeWhatsappPreRegistrationLink({
                    companyId,
                    storeId
                })
            );
            setNotice({ severity: "success", text: labels.linkRevoked });
        } catch (linkError) {
            setError(getErrorMessage(linkError, labels.linkActionError));
            clearDiagnostic();
        } finally {
            setActingStoreId("");
        }
    };

    if (loading) {
        return (
            <Box mt={3}>
                <Alert severity="info">{labels.loading}</Alert>
            </Box>
        );
    }
    if (!loadSucceeded) {
        return (
            <Box mt={3}>
                <Alert severity="error">{error || labels.loadError}</Alert>
                <Box mt={2}>
                    <Button onClick={loadSettings} variant="outlined">
                        {labels.retry}
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box mt={3}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                mb={2}
                style={{ gap: 16 }}
            >
                <Box>
                    <Typography variant="h5">{labels.title}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {labels.description}
                    </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                    <WhatsappPreRegistrationHelpControl labels={labels} />
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={saveSettings}
                        disabled={saving || !isDirty}
                    >
                        {saving ? labels.saving : labels.save}
                    </Button>
                </Box>
            </Box>
            <Box mb={2}>
                <Alert severity={featureActive ? "info" : "warning"}>
                    {featureActive
                        ? labels.rolloutActive
                        : labels.rolloutInactive}
                </Alert>
                {canManageFeature && (
                    <Box mt={1}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={featureActive}
                                    color="primary"
                                    disabled={changingFeature || isDirty}
                                    onChange={(event) =>
                                        void updateFeatureActive(
                                            event.target.checked
                                        )
                                    }
                                />
                            }
                            label={labels.rolloutEnabled}
                        />
                        <Typography variant="caption" color="textSecondary">
                            {labels.rolloutEnabledHelp}
                        </Typography>
                    </Box>
                )}
            </Box>
            {error && (
                <Box mb={2}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            )}
            {coverageDiagnostic && (
                <Box mb={2}>
                    <Alert severity="warning">
                        <WhatsappPreRegistrationCoverageDiagnosticAlert
                            coverageDiagnostic={coverageDiagnostic}
                            labels={labels}
                        />
                    </Alert>
                </Box>
            )}
            {notice && (
                <Box mb={2}>
                    <Alert severity={notice.severity}>{notice.text}</Alert>
                </Box>
            )}
            <Box mb={3}>
                <WhatsappPreRegistrationSetupGuide
                    steps={setupGuide}
                    labels={labels}
                />
            </Box>
            <WhatsappPreRegistrationConfigEditor
                config={settings.config}
                activeConfig={savedSettings.config}
                options={settings.options}
                labels={labels}
                createFlowModelLabel={t("whatsappPreRegistrationCreateModel")}
                isDirty={isDirty}
                onChange={updateConfig}
            />
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <WhatsappPreRegistrationLinks
                        config={
                            isDirty && savedSettings
                                ? savedSettings.config
                                : settings.config
                        }
                        generalEntry={savedSettings.generalEntry}
                        featureActive={featureActive}
                        stores={settings.options.stores}
                        links={links}
                        actingStoreId={actingStoreId}
                        actionsDisabled={isDirty}
                        labels={labels}
                        onRotate={rotateLink}
                        onRevoke={revokeLink}
                        onNotice={(text) =>
                            setNotice({ severity: "success", text })
                        }
                        onError={(linkError) => {
                            clearDiagnostic();
                            setError(
                                getErrorMessage(
                                    linkError,
                                    labels.linkActionError
                                )
                            );
                        }}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default WhatsappPreRegistrationSettings;
